import { Currency } from "@/shared/value-objects/currency.vo";
import { Money } from "@/shared/value-objects/money.vo";
import type {
  ControlDescriptorTagResponseDto,
  ControlListItemResponseDto,
  GetControlListResponseDto,
  GetControlSourceListRelationshipsResponseDto,
} from "@/spz-api/model";

export interface ProgressSegment {
  key: string;
  count: number;
  percentage: number;
  variant: string;
  labelKey: string;
}

export interface ControlListItem {
  id: string;
  shortId: string;
  control: string;
  resultCount: number;
  amount: Money;
  currency: Currency;
  tags: ControlTag[];
  parameters: {
    id: string;
    name: string;
    value: string;
    type: string;
  }[];
  progress: ProgressSegment[] | null;
}

export interface ControlTag {
  id: string;
  name: string;
}

export class ControlMapper {
  private static generateProgress(
    stepStats: ControlListItemResponseDto["workflowStats"],
    resultCount: number,
    steps: GetControlListResponseDto["relationships"]["workflowSteps"],
  ): ProgressSegment[] {
    // Compute percentages from counts
    const computePercentage = (count: number) =>
      resultCount > 0 ? (count / resultCount) * 100 : 0;

    return stepStats.counts.map((stepStat) => {
      const stepInfo = steps.find((s) => s.id === stepStat.stepId);
      return {
        key: stepStat.stepId,
        count: stepStat.count,
        percentage: computePercentage(stepStat.count),
        variant: stepInfo?.variant ?? "gray",
        labelKey: stepInfo ? stepInfo.labelKeyName : "unknown_step",
      };
    });
  }

  static toListItem(
    control: ControlListItemResponseDto,
    relationships: GetControlSourceListRelationshipsResponseDto,
  ): ControlListItem {
    const associatedCurrency = relationships.currencies.find(
      (c) => c.alphabeticalCode === control.currency,
    );
    const currency = associatedCurrency
      ? Currency.create(associatedCurrency)
      : Currency.EUR;
    const amount = Money.create(control.amount, currency);

    const tags = relationships.tags.filter((t) =>
      control.tagIds.includes(t.id),
    );

    const progress =
      control.resultCount > 0
        ? ControlMapper.generateProgress(
            control.workflowStats,
            control.resultCount,
            relationships.workflowSteps,
          )
        : null;

    return {
      id: control.id,
      shortId: control.legacyId?.toString() ?? control.id.slice(-8),
      control: control.control,
      resultCount: control.resultCount,
      amount,
      currency,
      tags: ControlTagMapper.toItems(tags),
      parameters: control.parameters.map((p) => ({
        id: p.id,
        name: p.name,
        value: String(p.value),
        type: p.type,
      })),
      progress,
    };
  }

  static toListItems(
    controls: ControlListItemResponseDto[],
    relationships: GetControlSourceListRelationshipsResponseDto,
  ): ControlListItem[] {
    return controls.map((control) =>
      ControlMapper.toListItem(control, relationships),
    );
  }
}

export class ControlTagMapper {
  static toItem(tag: ControlDescriptorTagResponseDto): ControlTag {
    return {
      id: tag.id,
      name: tag.name,
    };
  }

  static toItems(tags: ControlDescriptorTagResponseDto[]): ControlTag[] {
    return tags.map((tag) => ControlTagMapper.toItem(tag));
  }
}
